/**
 * SatelliteApprovals — admin review + approve/reject of satellite-tool requests.
 * Approval is the ONLY trigger for provisioning + the agent build (deny-by-default).
 * Polls every 5s while any request is mid-build.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

const STATUS = {
  new: ['#1d4ed8', '#dbeafe', 'In wachtrij'],
  needs_capability_review: ['#b45309', '#fef3c7', 'Capability-review nodig'],
  capability_drafting: ['#6d28d9', '#ede9fe', 'AI schrijft capability…'],
  capability_drafted: ['#b45309', '#fef3c7', 'Capability klaar voor review'],
  capability_rejected: ['#64748b', '#f1f5f9', 'Capability afgewezen'],
  approved: ['#0f766e', '#ccfbf1', 'Goedgekeurd'],
  provisioning: ['#0f766e', '#ccfbf1', 'Provisioning'],
  building: ['#6d28d9', '#ede9fe', 'Wordt gebouwd'],
  deployed: ['#0f766e', '#ccfbf1', 'Uitgerold'],
  live: ['#15803d', '#dcfce7', 'Live'],
  failed: ['#dc2626', '#fee2e2', 'Mislukt'],
  rejected: ['#64748b', '#f1f5f9', 'Afgewezen'],
};
const ACTIVE = new Set(['approved', 'provisioning', 'building', 'capability_drafting']);

export default function SatelliteApprovals() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/satellite-requests/all`);
      if (!r.ok) throw new Error((await r.json()).error || r.status);
      setRows(await r.json()); setErr(null);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(() => {
      setRows((cur) => { if (cur.some((x) => ACTIVE.has(x.status))) load(); return cur; });
    }, 5000);
    return () => clearInterval(timer.current);
  }, [load]);

  const act = async (id, action, body) => {
    setBusy(id);
    try {
      const opts = { method: 'POST', headers: { 'Content-Type': 'application/json' } };
      if (action === 'reject') opts.body = JSON.stringify({ reason: 'Afgewezen door admin' });
      else if (body) opts.body = JSON.stringify(body);
      const r = await authFetch(`${BACKEND_URL}/Api/satellite-requests/${id}/${action}`, opts);
      if (!r.ok) throw new Error((await r.json()).error || r.status);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const pill = (s) => {
    const [c, bg, label] = STATUS[s] || ['#475569', '#f1f5f9', s];
    return <span style={{ color: c, background: bg, fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999 }}>{label}</span>;
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h3 style={{ margin: '0 0 4px' }}>Satelliet-aanvragen</h3>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0 }}>
        Goedkeuren start provisioning + de AI-bouw. Aanvragen met een nieuwe backend-capability moeten eerst handmatig een endpoint krijgen.
      </p>
      {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {rows.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>Nog geen aanvragen.</div>}

      {rows.map((m) => (
        <div key={m.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div>
              <b style={{ fontSize: 15 }}>{m.display_name}</b>
              <span style={{ color: '#94a3b8', fontSize: 13 }}> /{m.tool_slug}</span>
            </div>
            {pill(m.status)}
          </div>
          <div style={{ fontSize: 13, color: '#475569', margin: '6px 0' }}>{m.goal_description}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            {m.requester_email} · capability: {m.requested_capability}
            {m.swa_url && <> · <a href={m.swa_url} target="_blank" rel="noreferrer" style={{ color: '#7c5cff' }}>{m.swa_url}</a></>}
          </div>
          {m.error_detail && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{m.error_detail}</div>}
          {(m.status === 'new') && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => act(m.id, 'approve')} disabled={busy === m.id}
                style={{ background: '#7c5cff', color: '#fff', border: 0, borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Goedkeuren & bouwen
              </button>
              <button onClick={() => act(m.id, 'reject')} disabled={busy === m.id}
                style={{ background: 'transparent', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 7, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
                Afwijzen
              </button>
            </div>
          )}
          {m.status === 'needs_capability_review' && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>
                Nieuwe backend-capability vereist. De AI kan een voorstel schrijven; jij reviewt de code vóór activatie.
              </div>
              <button onClick={() => act(m.id, 'draft-capability', { tool_slug: m.tool_slug, goal_description: m.goal_description })}
                disabled={busy === m.id}
                style={{ background: '#6d28d9', color: '#fff', border: 0, borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Capability laten schrijven (AI)
              </button>
            </div>
          )}
          {m.status === 'capability_drafting' && (
            <div style={{ fontSize: 12, color: '#6d28d9', marginTop: 8 }}>AI schrijft het endpoint… (dit ververst vanzelf)</div>
          )}
          {m.status === 'capability_drafted' && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>Voorstel — review de code vóór activatie:</div>
              {m.draft_summary && <div style={{ fontSize: 12, color: '#475569', margin: '4px 0' }}>{m.draft_summary}</div>}
              <pre style={{ maxHeight: 320, overflow: 'auto', background: '#0f1220', color: '#d6d9e8', fontSize: 11.5, lineHeight: 1.5, padding: 12, borderRadius: 8, whiteSpace: 'pre', margin: '6px 0' }}>
                {m.draft_diff || '(geen diff)'}
              </pre>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => act(m.id, 'apply-capability', {
                  tool_slug: m.tool_slug, goal_description: m.goal_description,
                  acceptance_criteria: m.acceptance_criteria || [],
                  draft_file: m.draft_file, draft_endpoint: m.draft_endpoint })}
                  disabled={busy === m.id}
                  style={{ background: '#15803d', color: '#fff', border: 0, borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  Goedkeuren & activeren
                </button>
                <button onClick={() => act(m.id, 'reject-capability')} disabled={busy === m.id}
                  style={{ background: 'transparent', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 7, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
                  Afwijzen
                </button>
              </div>
            </div>
          )}
          {m.status === 'capability_rejected' && (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Capability-voorstel afgewezen.</div>
          )}
        </div>
      ))}
    </div>
  );
}
